import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
} from '@mui/material';
import { cloudKitService } from '../services/cloudkit';

interface CloudKitConfigProps {
  onConfigured: () => void;
}

export function CloudKitConfig({ onConfigured }: CloudKitConfigProps) {
  const [apiToken, setApiToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await cloudKitService.configure(apiToken);
      localStorage.setItem('cloudkit_api_token', apiToken);
      onConfigured();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to configure CloudKit');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="80vh"
      p={2}
    >
      <Card sx={{ maxWidth: 500, width: '100%' }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Configure CloudKit
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Enter your CloudKit API token to connect to your iCloud database.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Get your token from:{' '}
            <Link
              href="https://icloud.developer.apple.com/dashboard/database/teams/8RV36K5JJ4/containers/iCloud.org.zimmelman.rz-sharing/environments/DEVELOPMENT/tokens"
              target="_blank"
              rel="noopener noreferrer"
            >
              CloudKit Dashboard
            </Link>
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="API Token"
              variant="outlined"
              value={apiToken}
              onChange={(e) => setApiToken(e.target.value)}
              margin="normal"
              required
              type="password"
            />
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={isLoading || !apiToken}
              sx={{ mt: 2 }}
            >
              {isLoading ? 'Connecting...' : 'Connect'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
